<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaignGroupList.modal.new.buttonText')"
      @click="campaignGroupModalOpen = true"
    />
    <export
      url="/campaign_groups/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.campaignGroups')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <campaign-group-modal
    v-model:campaign-group="editCampaignGroup"
    v-model:open="campaignGroupModalOpen"
    @edit-saved="refetch()"
    @new-saved="openCampaignGroup"
  />
  <qr-modal v-model:open="qrModalOpen" :campaign-group="currentCampaignGroup" />

  <div class="ml-12">
    <PayoutStatisticsTable
        :statistics="{
          total: filteredStatistics?.total,
          filtered: filteredStatistics?.filtered,
          max: filteredStatistics?.max,
        }"
        :loading="loadingStatistics"
        :hide-selected="true"
    />
    <div v-if="isFinanceAdmin" class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
      {{ $t('navigation.campaigns.groups') }}
    </div>
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :row-link="
          (campaignGroup) => ({
            name: 'campaign-group-details',
            params: { campaignGroupId: campaignGroup.id },
          })
        "
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="actions"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {
  ref, reactive, computed, unref,
} from 'vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import { useI18n } from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@components/utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type { HydraCollection, CampaignGroup, Company } from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import CampaignGroupModal from '@pages/campaigns/CampaignGroupModal.vue';
import { useRouter } from 'vue-router';
import type { Campaign } from '@/entities/Entities';
import { useDelete } from '@hooks/api';
import Export from '@utils/toolbar/panes/Export.vue';
import {formatTimeDurationFromSeconds} from '@/utils/helpers';
import { useQueryRef } from '@/hooks/url';
import PayoutStatisticsTable, {Statistics} from '@utils/PayoutStatisticsTable.vue';
import {useStore} from '@/store';
import {useTableFields} from '@hooks/table-configuration';
import QrModal from '@pages/campaigns/QrModal.vue';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const campaignGroupModalOpen = ref(false);
const editCampaignGroup = ref<CampaignGroup | undefined>(undefined);

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();


const [deleteCampaignGroup, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const currentCampaignGroup = ref();
const qrModalOpen = ref(false);

const router = useRouter();

const openCampaignGroup = (id: string) => {
  router.push({
    name: 'campaign-group-details',
    params: { campaignGroupId: id },
  });
};

const actions = [
  {
    title: t('pages.campaignGroupList.actions.edit'),
    action: (item: CampaignGroup) => {
      editCampaignGroup.value = { ...item };
      campaignGroupModalOpen.value = true;
    },
  },
  {
    title: t('pages.campaignGroupList.actions.qrCode'),
    action: (item: CampaignGroup) => {
      currentCampaignGroup.value = item;
      qrModalOpen.value = true;
    },
  },
  {
    title: t('pages.campaignGroupList.actions.delete'),
    action: async (item: Campaign) => {
      if (await deleteCampaignGroup(`/campaign_groups/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaignGroupList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'defaultStartDate',
    title: t('campaignGroup.defaultStartDate'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.defaultStartDate'),
      },
    },
  },
  {
    key: 'defaultEndDate',
    title: t('campaignGroup.defaultEndDate'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.defaultEndDate'),
      },
    },
  },
  {
    key: 'totalDrivers',
    title: t('campaign.bookingNumbers'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (group: CampaignGroup) => {

      return `${group.bookingNumbers?.totalBooked || 0} / ${group.bookingNumbers?.totalSlots || 0}`;
    }
  },
  {
    key: 'name',
    title: t('campaignGroup.name'),
    sortable: true,
    visible: true,
    group: t('pages.campaignGroupList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.name'),
      },
    },
  },
  {
    key: 'timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
      mixed: t('campaign.timeStatus.mixed'),
      null: t('campaign.timeStatus.null')
    },
    title: t('campaign.timeStatus.DEFAULT2'),
    sortable: false,
    visible: true,
    group: t('pages.campaignGroupList.fieldGroups.general'),
    formatter: (v: string) => t(`campaign.timeStatus.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
          {
            label: t('campaign.timeStatus.mixed'),
            value: 'mixed',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT2'),
      },
    },
  },
  {
    key: 'description',
    title: t('campaignGroup.description.DEFAULT'),
    sortable: true,
    visible: true,
    group: t('pages.campaignGroupList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.description.DEFAULT'),
      },
    },
  },
  {
    key: 'ownerCompany.name',
    title: t('campaignGroup.ownerCompany'),
    sortable: true,
    visible: false,
    group: t('pages.campaignGroupList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Company>('/companies', (value) => ({
          label: value.name,
          value: value.name,
        })),
        label: t('campaignGroup.ownerCompany'),
      },
    },
  },
  {
    key: 'contactEmail',
    title: t('campaignGroup.contactEmail'),
    sortable: true,
    visible: true,
    group: t('pages.campaignGroupList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.contactEmail'),
      },
    },
  },
  {
    key: 'contractConfirmed',
    title: t('campaignGroup.contractConfirmed'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.contractConfirmed'),
      },
    },
  },
  {
    key: 'expressBooking',
    title: t('campaignGroup.expressBooking'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.expressBooking'),
      },
    },
  },
  {
    key: 'trackedDistance',
    title: t('campaign.trackedDistance'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => `${(v / 1000).toFixed(2)} Km`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>km</span>,
        label: t('campaign.trackedDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'trackedTime',
    title: t('campaign.trackedTime'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('campaign.trackedTime'),
      },
    },
  },
  {
    key: 'kcal',
    title: t('driver.kcal'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'kcal',
    formatter: (v: number) => (v ? v.toFixed(2) : 0) + ' kcal',
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>kcal</span>,
        label: t('driver.kcal'),
      },
    },
  },
  {
    key: 'co2',
    title: t('driver.co2'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'kg',
    formatter: (v: number) => (v ? v.toFixed(2) : 0) + ' kg',
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>kg</span>,
        label: t('driver.co2'),
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('campaign.numOfTours'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTours'),
      },
    },
  },
  {
    key: 'numOfTopPhotos',
    title: t('campaign.numOfTopPhotos'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTopPhotos'),
      },
    },
  },
  {
    key: 'numOfDriversWithNoticeComment',
    title: t('campaign.numOfDriversWithNoticeComment'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfDriversWithNoticeComment'),
      },
    },
  },
  {
    key: 'avgSpeed',
    title: t('campaign.avgSpeed'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => `${(v * 3.6).toFixed(2)} Km/h`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>km/h</span>,
        label: t('campaign.avgSpeed'),
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'createdAt',
    title: t('campaignGroup.createdAt'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.createdAt'),
      },
    },
  },
  {
    key: 'qrToken',
    title: t('campaignGroup.qrCode'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.qrCode'),
      },
    },
  }
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<Statistics>('/campaign_groups/overall-payout-statistics', providerParams);

const {
  data: items,
  loading,
  refetch: refetchItems,
} = useProvider<HydraCollection<CampaignGroup>>('/campaign_groups', providerParams);

const refetch = () => {
  refetchItems();
  refetchStatistics();
}

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const auth = useStore()
const isFinanceAdmin = computed(() => auth.getters['auth/IS_FINANCE_ADMIN']);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
